import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, FolderTree, Tag } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Categories() {
  const qc = useQueryClient();
  const [catName, setCatName] = useState("");
  const [brandName, setBrandName] = useState("");
  const [open, setOpen] = useState(null); // 'cat' | 'brand'

  const { data: categories } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/admin/categories")).data });
  const { data: brands } = useQuery({ queryKey: ["brands"], queryFn: async () => (await api.get("/admin/brands")).data });

  const addCategory = useMutation({
    mutationFn: async () => (await api.post("/admin/categories", { name: catName })).data,
    onSuccess: () => { toast.success("Category created"); setCatName(""); setOpen(null); qc.invalidateQueries({ queryKey: ["categories"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const addBrand = useMutation({
    mutationFn: async () => (await api.post("/admin/brands", { name: brandName })).data,
    onSuccess: () => { toast.success("Brand created"); setBrandName(""); setOpen(null); qc.invalidateQueries({ queryKey: ["brands"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const delCategory = useMutation({
    mutationFn: async (id) => api.delete(`/admin/categories/${id}`),
    onSuccess: () => { toast.success("Category removed"); qc.invalidateQueries({ queryKey: ["categories"] }); },
  });
  const delBrand = useMutation({
    mutationFn: async (id) => api.delete(`/admin/brands/${id}`),
    onSuccess: () => { toast.success("Brand removed"); qc.invalidateQueries({ queryKey: ["brands"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div data-testid="categories-page">
      <PageHeader title="Categories & Brands" sub="Master catalog taxonomy. Agents inherit this structure automatically." testid="categories-title">
        <Button data-testid="add-category-btn" onClick={() => setOpen("cat")} className="bg-orange-500 text-white hover:bg-orange-600"><Plus size={14} /> Category</Button>
        <Button data-testid="add-brand-btn" variant="outline" onClick={() => setOpen("brand")} className="border-slate-700 text-slate-300 hover:bg-slate-800"><Tag size={14} /> Brand</Button>
      </PageHeader>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="categories-list">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-200"><FolderTree size={15} className="text-orange-400" /> Categories</h3>
          {categories?.length ? (
            <div className="space-y-1.5">
              {categories.map((c) => (
                <div key={c.id} data-testid={`category-row-${c.slug}`} className="flex items-center justify-between rounded-lg bg-slate-900/60 px-3 py-2">
                  <span className="text-sm text-slate-200">{c.name}</span>
                  <button data-testid={`delete-category-${c.slug}`} onClick={() => delCategory.mutate(c.id)} className="text-slate-600 transition-colors hover:text-rose-400">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : <EmptyState title="No categories yet" />}
        </div>
        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="brands-list">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-200"><Tag size={15} className="text-orange-400" /> Brands</h3>
          {brands?.length ? (
            <div className="flex flex-wrap gap-2">
              {brands.map((b) => (
                <span key={b.id} data-testid={`brand-${b.name}`} className="flex items-center gap-1.5 rounded-full border border-slate-700 bg-slate-900/60 px-3 py-1.5 text-xs text-slate-300">
                  {b.name}
                  <button data-testid={`delete-brand-${b.name}`} onClick={() => delBrand.mutate(b.id)} className="text-slate-600 transition-colors hover:text-rose-400">×</button>
                </span>
              ))}
            </div>
          ) : <EmptyState title="No brands yet" />}
        </div>
      </div>

      <Dialog open={!!open} onOpenChange={() => setOpen(null)}>
        <DialogContent className="border-slate-800 bg-[#111827] text-slate-100">
          <DialogHeader>
            <DialogTitle>{open === "cat" ? "New Category" : "New Brand"}</DialogTitle>
          </DialogHeader>
          <Input
            data-testid="taxonomy-name-input"
            value={open === "cat" ? catName : brandName}
            onChange={(e) => (open === "cat" ? setCatName(e.target.value) : setBrandName(e.target.value))}
            placeholder={open === "cat" ? "e.g. Electronics" : "e.g. Sony"}
            className="border-slate-700 bg-slate-900 text-slate-100"
          />
          <Button
            data-testid="taxonomy-save-btn"
            disabled={(open === "cat" ? addCategory : addBrand).isPending}
            onClick={() => (open === "cat" ? addCategory.mutate() : addBrand.mutate())}
            className="bg-orange-500 text-white hover:bg-orange-600">
            Create
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
